import { UserCheck, UserX } from 'lucide-react';
import { SearchInput } from '../../components/SearchInput';
import { InlineLoader } from '../../components/loading';
import { formatIsoDay } from '../../utils/dateHelpers';
import type { TableCell, TableSheet } from '../../utils/tableXlsx';
import { monthName } from './AwaitingUploadsNotice';

/**
 * Employee Score → View → "Inactive employees".
 *
 * The page lists the people who are active today, for every month. This tab is
 * the other half: people who have since left, with the score that was SAVED for
 * them for the month on screen while they were still active. Nothing here is
 * recalculated and nobody is re-ranked, so no rank, tile or Bottom Box — those
 * belong to the current team (API: inactive-employee-scores.service).
 */

export type StaffView = 'active' | 'inactive';

export interface InactiveScoreRow {
  employee_code: string;
  employee_name: string;
  role_id: string | null;
  role_name: string | null;
  store_code: string;
  store_name: string;
  city: string;
  manager_code: string | null;
  manager_name: string;
  /**
   * When they left, YYYY-MM-DD, by the Attrition dashboard's rule: the earlier of
   * the last working day and the day 1Office made them inactive. Null when 1Office
   * holds no usable date.
   */
  exit_date: string | null;
  exit_date_source: string | null;
  total: number;
  /** Band as saved; null for a month saved before bands were. */
  quadrant: string | null;
  /** The day the score was last saved, YYYY-MM-DD (IST). */
  saved_at: string | null;
  metrics: Array<{ code: string; name: string; contribution: number; weight_pct: number; display_order: number }>;
}

const PLACEHOLDER = '—';

/** '3 Sep 2026' from an ISO day or timestamp — read off the string, so no time zone can move it. */
export const dayLabel = (iso: string | null): string =>
  (/^\d{4}-\d{2}-\d{2}/.test(String(iso ?? '')) ? formatIsoDay(String(iso)) : PLACEHOLDER);

export interface InactiveMetricColumn { code: string; name: string }

/**
 * The parameter columns of the rows on screen, in scorecard order. Parameter
 * figures were first saved in October 2026, so an earlier month has none and the
 * table shows the total alone.
 */
export const inactiveMetricColumns = (rows: InactiveScoreRow[]): InactiveMetricColumn[] => {
  const seen = new Map<string, { name: string; order: number }>();
  rows.forEach((r) => r.metrics.forEach((m) => {
    if (!seen.has(m.code)) seen.set(m.code, { name: m.name, order: m.display_order });
  }));
  return [...seen.entries()]
    .sort((a, b) => a[1].order - b[1].order || a[0].localeCompare(b[0]))
    .map(([code, v]) => ({ code, name: v.name }));
};

/**
 * Role first, then the search box: name, code, store or manager.
 *
 * A leaver whose role is no longer on record is kept under EVERY role: the page
 * always has a role picked, and hiding them there would hide them altogether.
 */
export const visibleInactiveRows = (
  rows: InactiveScoreRow[], roleId: string | null, search: string,
): InactiveScoreRow[] => {
  const scoped = roleId ? rows.filter((r) => r.role_id === roleId || r.role_id === null) : rows;
  const q = search.trim().toLowerCase();
  if (!q) return scoped;
  return scoped.filter((r) => [
    r.employee_name, r.employee_code, r.store_name, r.store_code, r.manager_name, r.manager_code,
  ].some((v) => String(v ?? '').toLowerCase().includes(q)));
};

/** The table on screen as an Excel sheet. Built by position: two parameters can share a name. */
export const buildInactiveScoreSheet = (rows: InactiveScoreRow[]): TableSheet => {
  const columns = inactiveMetricColumns(rows);
  const headers = [
    'Employee Name', 'Employee Code', 'Last Store', 'Store Code', 'Reporting Manager', 'Exit Date',
    ...columns.map((c) => c.name), 'Score', 'Band', 'Score Saved On',
  ];
  const numberFormats: Record<number, string> = {};
  columns.forEach((_, i) => { numberFormats[6 + i] = '0.00'; });
  numberFormats[6 + columns.length] = '0.00';
  return {
    name: 'Inactive employees',
    headers,
    numberFormats,
    rows: rows.map((r): TableCell[] => {
      const byCode = new Map(r.metrics.map((m) => [m.code, m.contribution]));
      return [
        // What the screen prints: the name, or the code when no name is on record.
        r.employee_name, r.employee_code, r.store_name, r.store_code, r.manager_name || r.manager_code || '',
        r.exit_date ?? '',
        ...columns.map((c) => byCode.get(c.code) ?? null),
        r.total, r.quadrant ?? '', r.saved_at ? r.saved_at.slice(0, 10) : '',
      ];
    }),
  };
};

interface StripProps {
  view: StaffView;
  onChange: (view: StaffView) => void;
  /** Null while a count is not known yet (loading, or the list could not be read). */
  activeCount: number | null;
  inactiveCount: number | null;
}

/**
 * Active / Inactive, each with the number that matches the role picked. The strip
 * every master page uses: light tray, white pill for the open tab, icon then label.
 */
export function StaffViewTabs({ view, onChange, activeCount, inactiveCount }: StripProps) {
  const tabs: { key: StaffView; label: string; icon: typeof UserCheck; count: number | null }[] = [
    { key: 'active', label: 'Active employees', icon: UserCheck, count: activeCount },
    { key: 'inactive', label: 'Inactive employees', icon: UserX, count: inactiveCount },
  ];
  return (
    <div
      className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1"
      role="tablist"
      aria-label="Employees"
    >
      {tabs.map((t) => {
        const Icon = t.icon;
        const isActive = view === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
              isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Icon className="h-4 w-4" />
            {t.label}
            {t.count !== null ? ` (${t.count.toLocaleString()})` : ''}
          </button>
        );
      })}
    </div>
  );
}

interface PanelProps {
  /** Already narrowed to the role picked and the search text. */
  rows: InactiveScoreRow[];
  month: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  search: string;
  onSearch: (value: string) => void;
  /** The role picked on the page, for the empty message; null when every role is shown. */
  roleName?: string | null;
}

export default function InactiveScoresPanel({
  rows, month, loading, error, onRetry, search, onSearch, roleName = null,
}: PanelProps) {
  const columns = inactiveMetricColumns(rows);
  const th = 'whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const td = 'px-4 py-3 text-sm text-gray-700';

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        People who have left, with the score saved for them for {monthName(month)} while they were
        still active. Scores are shown as they were saved and nobody is re-ranked. A score saved
        before a late upload arrived does not include that upload. Someone who was made inactive
        before any score was saved for {monthName(month)} is not listed.
      </p>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-gray-50/70 p-2">
        <SearchInput value={search} onChange={onSearch} />
        <span className="ml-auto text-xs font-medium text-gray-500">
          {!loading && !error ? `${rows.length.toLocaleString()} employee${rows.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm text-gray-500">
          <InlineLoader label="Loading the employees who have left…" className="flex justify-center" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}{' '}
          <button type="button" onClick={onRetry} className="font-medium underline hover:no-underline">
            Try again
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <h3 className="text-base font-medium text-gray-600">
            {search.trim()
              ? 'No one who has left matches the search'
              : roleName
                ? `No one who has left the ${roleName} role has a saved score for ${monthName(month)}`
                : `No one who has left has a saved score for ${monthName(month)}`}
          </h3>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={th}>Employee Name</th>
                  <th className={th}>Employee Code</th>
                  <th className={th}>Last Store</th>
                  <th className={th}>Reporting Manager</th>
                  <th className={th}>Exit Date</th>
                  {columns.map((c) => (
                    <th key={c.code} className={`${th} text-right`}>{c.name}</th>
                  ))}
                  <th className={`${th} text-right`}>Score</th>
                  <th className={th}>Band</th>
                  <th className={th}>Score Saved On</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => {
                  const byCode = new Map(r.metrics.map((m) => [m.code, m.contribution]));
                  return (
                    <tr key={r.employee_code}>
                      <td className={`${td} font-medium text-gray-900`}>{r.employee_name || PLACEHOLDER}</td>
                      <td className={td}>{r.employee_code}</td>
                      <td className={td}>
                        {r.store_name || PLACEHOLDER}
                        {r.store_code && <span className="block text-xs text-gray-500">{r.store_code}</span>}
                      </td>
                      <td className={td}>{r.manager_name || r.manager_code || PLACEHOLDER}</td>
                      <td className={`${td} whitespace-nowrap`}>{dayLabel(r.exit_date)}</td>
                      {columns.map((c) => {
                        const v = byCode.get(c.code);
                        return (
                          <td key={c.code} className={`${td} text-right tabular-nums`}>
                            {v === undefined ? PLACEHOLDER : v.toFixed(2)}
                          </td>
                        );
                      })}
                      <td className={`${td} text-right font-semibold tabular-nums text-gray-900`}>{r.total.toFixed(2)}</td>
                      <td className={td}>{r.quadrant || PLACEHOLDER}</td>
                      <td className={`${td} whitespace-nowrap text-gray-500`}>{dayLabel(r.saved_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
